import { useEffect, useState } from "react";
import useTspApiResource from "./useTspApiResource";

// A server-filtered, server-paged list that grows as "Load more" is pressed.
//
//   const list = useTspPagedList((page) => api.list({ ...params, page, limit }), paramsKey);
//
// `paramsKey` (a string) identifies the current filter set: changing it starts
// again from page 1 with nothing shown until that page arrives. Rows already
// shown are carried into the next page's state, so `items` is always derived
// from the current request plus what was carried — never one render behind the
// data, which would flash a false empty state. Rows are de-duplicated by
// `getId` (default `entry.id`). The last known `meta` (total, per-type counts,
// ...) is kept while a further page loads. A failed request surfaces as `error`
// — never as made-up rows.
export default function useTspPagedList(fetchPage, paramsKey, getId = (entry) => entry.id) {
  const [pageState, setPageState] = useState({ key: paramsKey, page: 1, carried: [] });
  // A page number belongs to one set of filters: changing a filter is page 1 again.
  const { page, carried } = pageState.key === paramsKey ? pageState : { key: paramsKey, page: 1, carried: [] };
  const [lastMeta, setLastMeta] = useState(null);

  const resource = useTspApiResource(() => fetchPage(page), [paramsKey, page]);

  useEffect(() => {
    if (resource.meta) setLastMeta(resource.meta);
  }, [resource.meta]);

  const seen = new Set(carried.map(getId));
  const items = [...carried, ...(resource.data || []).filter((entry) => !seen.has(getId(entry)))];
  const meta = resource.meta || lastMeta;

  return {
    items,
    meta,
    loading: resource.loading && page === 1,
    loadingMore: resource.loading && page > 1,
    error: resource.error,
    remaining: meta ? Math.max(0, meta.total - items.length) : 0,
    loadMore: () => setPageState({ key: paramsKey, page: page + 1, carried: items }),
    reload: () => {
      setPageState({ key: paramsKey, page: 1, carried: [] });
      resource.reload();
    },
  };
}
